import React, { useState } from 'react';
import { Button, Card, Segmented, Space, Table, Tag, Typography } from 'antd';
import { DownloadOutlined, ReloadOutlined, TrophyOutlined } from '@ant-design/icons';
import {
  LeaderboardPeriod,
  LeaderboardRowDto,
  useAdminLeaderboard,
} from '@shared/api/hooks/useLeaderboard';

const PERIOD_OPTIONS: { label: string; value: LeaderboardPeriod }[] = [
  { label: 'Mensile', value: 'monthly' },
  { label: 'Stagionale', value: 'season' },
  { label: 'All-time', value: 'alltime' },
];

function downloadCsv(rows: LeaderboardRowDto[], period: string): void {
  const header = ['posizione', 'utente', 'email', 'punti'];
  const escape = (v: string | number | null | undefined): string =>
    v == null ? '' : `"${String(v).replace(/"/g, '""')}"`;
  const csv = [
    header.join(','),
    ...rows.map((r) =>
      [r.position, escape(r.displayName ?? ''), escape(r.email ?? ''), r.points].join(','),
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `leaderboard-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function LeaderboardPage(): React.ReactElement {
  const [period, setPeriod] = useState<LeaderboardPeriod>('monthly');
  const [page, setPage] = useState(1);
  const limit = 50;

  const { data, isLoading, refetch, isFetching } = useAdminLeaderboard({ period, page, limit });
  const rows = data?.data ?? [];

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Classifica
          </Typography.Title>
          <Tag>{data?.meta.total ?? 0}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Segmented
            options={PERIOD_OPTIONS}
            value={period}
            onChange={(v) => {
              setPeriod(v as LeaderboardPeriod);
              setPage(1);
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button
            icon={<DownloadOutlined />}
            disabled={rows.length === 0}
            onClick={() => downloadCsv(rows, period)}
          >
            Esporta CSV
          </Button>
        </Space>
      }
    >
      <Table<LeaderboardRowDto>
        rowKey="userId"
        loading={isLoading}
        dataSource={rows}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: limit,
          total: data?.meta.total ?? 0,
          showSizeChanger: false,
          onChange: setPage,
        }}
        columns={[
          {
            title: 'Pos',
            dataIndex: 'position',
            width: 80,
            align: 'center',
            render: (v: number) => {
              if (v === 1) return <Tag color="gold">🥇 #{v}</Tag>;
              if (v === 2) return <Tag color="silver">🥈 #{v}</Tag>;
              if (v === 3) return <Tag color="orange">🥉 #{v}</Tag>;
              return <Typography.Text strong>#{v}</Typography.Text>;
            },
          },
          {
            title: 'Utente',
            render: (_v, r) =>
              r.displayName ?? <Typography.Text type="secondary">—</Typography.Text>,
          },
          {
            title: 'Email',
            dataIndex: 'email',
            ellipsis: true,
            render: (v: string | undefined) => v ?? '—',
          },
          {
            title: 'Punti',
            dataIndex: 'points',
            width: 130,
            align: 'right',
            sorter: (a, b) => a.points - b.points,
            defaultSortOrder: 'descend',
            render: (v: number) => <Typography.Text strong>{v.toLocaleString('it-IT')}</Typography.Text>,
          },
        ]}
        locale={{
          emptyText: (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <TrophyOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
              <div style={{ marginTop: 8, color: '#8c8c8c' }}>
                Classifica vuota per il periodo selezionato.
              </div>
            </div>
          ),
        }}
      />
    </Card>
  );
}
